import { BaseError, ImageError, Option, Result } from "@inrev/core";
import { toBlob } from "html-to-image";

export class ClipboardWriteError extends BaseError {
	readonly name = "ClipboardWriteError";
}

export type CopyOrgChartImageError = ImageError | ClipboardWriteError;

const OMIT_ATTRIBUTE = "data-orgchart-image-omit";

/** Spread onto an element inside the chart card to leave it out of the chart image. */
export const omittedFromOrgChartImage = { [OMIT_ATTRIBUTE]: "true" };

const includeInOrgChartImage = (node: Node): boolean =>
	!(node instanceof Element) || node.getAttribute(OMIT_ATTRIBUTE) !== "true";

const SVG_PAINT = [
	"fill",
	"stroke",
	"stroke-width",
	"stroke-dasharray",
	"stroke-linecap",
	"stroke-linejoin",
	"opacity",
	"font-family",
	"font-size",
	"font-weight",
] as const;

/**
 * The capture deep-clones the SVG and does not reapply class styles inside it, so a Tailwind
 * `fill-*` becomes the SVG default, black. Copy the computed paint onto the shapes.
 */
const inlineSvgPaint = (root: HTMLElement): void => {
	for (const node of root.querySelectorAll("svg *")) {
		if (!(node instanceof SVGElement)) continue;
		const computed = getComputedStyle(node);
		for (const name of SVG_PAINT) {
			const value = computed.getPropertyValue(name);
			if (value.length > 0) node.style.setProperty(name, value);
		}
	}
};

const scrollsSideways = (node: Element): node is HTMLElement =>
	node instanceof HTMLElement &&
	getComputedStyle(node).overflowX !== "visible" &&
	node.scrollWidth - node.clientWidth > 1;

/**
 * A diagram that scrolls inside the card is clipped in the snapshot. Widen the copy by the hidden
 * amount so the row stays side by side, and let the scroller overflow so no scrollbar is painted.
 */
const unclip = (copy: HTMLElement, width: number): void => {
	const scrollers = [...copy.querySelectorAll("*")].filter(scrollsSideways);
	const hidden = Math.max(0, ...scrollers.map((node) => node.scrollWidth - node.clientWidth));
	copy.style.setProperty("width", `${width + hidden}px`);
	for (const scroller of scrollers) scroller.style.setProperty("overflow", "visible");
};

const paint = (element: HTMLElement, skipFonts: boolean): Promise<Blob | null> => {
	const width = element.offsetWidth;
	const height = element.offsetHeight;
	return toBlob(element, {
		backgroundColor: getComputedStyle(element).backgroundColor,
		filter: includeInOrgChartImage,
		height: height > 0 ? height : undefined,
		pixelRatio: 2,
		skipFonts,
		width: width > 0 ? width : undefined,
	});
};

const hasPixels = (blob: Blob | null): blob is Blob => Option.isSome(blob) && blob.size > 0;

const stageOffscreen = (copy: HTMLElement, width: number): HTMLElement => {
	const stage = document.createElement("div");
	stage.style.setProperty("position", "fixed");
	stage.style.setProperty("top", "0");
	stage.style.setProperty("left", "-100000px");
	copy.style.setProperty("width", `${width}px`);
	stage.append(copy);
	document.body.append(stage);
	return stage;
};

/** A failed font embed is retried without fonts so the picture still comes out. */
const paintOffscreen = async (copy: HTMLElement, width: number): Promise<Blob | null> => {
	const stage = stageOffscreen(copy, width);
	try {
		unclip(copy, width);
		inlineSvgPaint(copy);
		const embedded = await paint(copy, false).catch(() => null);
		return hasPixels(embedded) ? embedded : await paint(copy, true);
	} finally {
		stage.remove();
	}
};

/**
 * PNG of the chart card, wide enough to include a diagram that scrolls inside the card. It paints
 * a copy staged off screen, so the chart on the page never moves.
 */
export const orgChartCardToPng = async (card: HTMLElement): Promise<Result<ImageError, Blob>> => {
	const copy = card.cloneNode(true);
	if (!(copy instanceof HTMLElement)) {
		return Result.err(new ImageError("Could not copy the chart card"));
	}
	const painted = await Result.fromTryAsync(
		() => paintOffscreen(copy, card.offsetWidth),
		() => new ImageError("Could not paint the chart card"),
	);
	return painted.flatMap((blob) =>
		hasPixels(blob)
			? Result.ok(blob)
			: Result.err(new ImageError("The chart image came out empty")),
	);
};

export const canCopyImages = (): boolean =>
	typeof ClipboardItem !== "undefined" && Option.isSome(navigator.clipboard);

/**
 * `ClipboardItem` takes the PNG as a promise that rejects on failure. The rejection is marked
 * handled here because `copyOrgChartCardPng` reports it, and a browser that drops the item without
 * reading it would otherwise raise an unhandled rejection.
 */
const clipboardPng = (png: Promise<Result<ImageError, Blob>>): Promise<Blob> => {
	const blob = png.then((result) =>
		result.match(
			(value) => Promise.resolve(value),
			(error) => Promise.reject(error),
		),
	);
	blob.catch(() => undefined);
	return blob;
};

/**
 * Copies the chart card to the clipboard as a PNG. Call it directly in the click handler: the
 * browser drops the gesture if `clipboard.write` waits for the capture, so the clipboard gets a
 * promise of the PNG instead. A failed capture also fails the write, so the capture error wins.
 */
export const copyOrgChartCardPng = async (
	card: HTMLElement,
): Promise<Result<CopyOrgChartImageError, void>> => {
	const png = orgChartCardToPng(card);
	const written = await Result.fromTryAsync(
		() => navigator.clipboard.write([new ClipboardItem({ "image/png": clipboardPng(png) })]),
		() => new ClipboardWriteError("Could not write the chart image"),
	);
	return (await png).flatMap(() => written);
};

export const orgChartImageFileName = (reportingEntity: string): string => {
	const slug = reportingEntity
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
	return `${slug.length > 0 ? slug : "org-chart"}.png`;
};
